<template>
    <div class="feedback-operation-wrapper" :class="[feedbackOperationData.status.toLowerCase(), {'mobile': getMobileAppState}, variant]">
        <div class="aligner-wrapper">
            <component :is="iconComponent" class="icon-component"/>
            <!-- Title and Buttons wrapper -->
            <div class="title-and-buttons-wrapper">
                <!-- Title -->
                <div class="title">{{feedbackOperationData.title}}</div>
                <!-- Buttons Data -->
                <buttons-operations-group
                v-if="feedbackOperationData.buttonsData"
                :notificationOnlyEmail="feedbackOperationData.buttonsData.notificationOnlyEmail"
                :emailAction="feedbackOperationData.buttonsData.emailAction"
                :smsAction="feedbackOperationData.buttonsData.smsAction"
                :sendPayload="feedbackOperationData.buttonsData.sendPayload"
                :downloadUrl="feedbackOperationData.buttonsData.downloadUrl"
                :multipleDownloadUrls="feedbackOperationData.buttonsData.multipleDownloadUrls"
                :operationId="feedbackOperationData.buttonsData.operationId"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import ButtonsOperationsGroup from '@/components/generic/buttons/OperationsGroup'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
const { getMobileAppState } = mapGetters()

/* Props
@variant {String} can be [none(default), widget]
@iconsColorReversed {Boolean}
@feedbackOperationData {Object} - object with:
    - status {String} used for color and icon [info, success, error, warning]
    - title: {String} used to display in this component
    - buttonsData {Object} - info to create buttons and their login, object with
        emailAction {String} action name so component call it, example: 'sendOperationEmailNotification'
        smsAction {String} action name so component call it, example: 'sendOperationSMSNotification'
        sendPayload {Object} send actions payload
        downloadUrl {String} url string for download,
        operationId {String, Number} id used for shareLink, email/sms
*/
const props = defineProps({
    variant: String,
    iconsColorReversed: Boolean,
    feedbackOperationData: Object
})

const iconsMapper = {
	info: 'success',
	error: 'error',
	warning: 'pending'
}

const iconComponent = computed(() => {
	const iconSelected = iconsMapper[props.feedbackOperationData?.status.toLowerCase()] + (props.iconsColorReversed ? '-color-reversed' : '')
    return defineAsyncComponent(() => import('@/assets/icons/illustrations/operationFeedback/' + iconSelected + '.svg?inline'))
})
</script>

<style lang="scss" scoped>
.feedback-operation-wrapper {
    width: 100%;
    height: 144px;
    margin-bottom: 24px;
    padding: 0 24px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 16px 16px 0px 0px;
    overflow: hidden;

    &.info, &.success {
        background-color: var(--positive);
    }

    &.error {
        background-color: var(--negative);
    }

    &.warning {
        background-color: var(--warning);
    }

    .aligner-wrapper {
        width: 100%;
        max-width: 508px;
        height: 100%;

        display: flex;
        align-items: center;
        gap: 32px;

        .icon-component {
            width: 80px;
            height: 80px;

            flex-shrink: 0;
        }

        .title-and-buttons-wrapper {
            width: 100%;

            display: flex;
            flex-direction: column;
            gap: 8px;

            .title {
                @include H3-Medium;
                color: var(--secondary-light-4);
            }
        }
    }

    &.mobile, &.widget {
        height: auto;
        padding-top: 72px;

        .aligner-wrapper {
            flex-direction: column;
            gap: 16px;
            margin-bottom: 24px;

            .icon-component {
                width: 56px;
                height: 56px;

                flex-shrink: 0;
            }

            .title-and-buttons-wrapper {
                width: 100%;

                display: flex;
                flex-direction: column;
                gap: 16px;

                .title {
                    @include H3-Medium;
                    color: var(--secondary-light-4);
                    text-align: center;
                }

                .operations-buttons-group {
                    justify-content: center;
                }
            }
        }
    }

    &.widget {
        padding-top: 32px;
        background-color: transparent;

        &.info, &.success {
            .aligner-wrapper {
                .title-and-buttons-wrapper {
                    .title {
                        @include H4-Medium;
                        color: var(--positive);
                    }
                }
            }
        }

        &.error {
            .aligner-wrapper {
                .title-and-buttons-wrapper {
                    .title {
                        @include H4-Medium;
                        fill: var(--negative);
                        color: var(--negative);
                    }
                }
            }
        }

        &.warning {
            .aligner-wrapper {
                .title-and-buttons-wrapper {
                    .title {
                        @include H4-Medium;
                        color: var(--warning);
                    }
                }
            }
        }

        .aligner-wrapper {
            height: auto;

            .icon-component {
                width: 72px;
                height: 72px;
            }

            .title-and-buttons-wrapper {
                gap: 32px;
            }
        }
    }
}
</style>
